import React, { useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { MdFileDownload } from "react-icons/md";

import api from "../../services/api";

// The six files of the PILOTAGE workbook. Column names, order and formatting mirror it exactly so
// an export can be pasted straight over a tab without touching the formulas built on top.
// Only source columns are exported — the sheet's computed columns (SURCONSO ?, diff en jours,
// Concat, ExitDateFixed…) stay in the sheet, where their rules can be changed without a deploy.

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3];

// `/activity/search` caps at 10 000 rows server-side with no pagination. Hitting the cap silently
// truncates the file, so we warn rather than hand over an incomplete export.
const ACTIVITY_LIMIT = 10000;

// Dates as D/M/YYYY, no leading zeros — the workbook's format.
const dt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

// Decimal comma, integers left bare. The quoting below then wraps decimals, which is what the
// workbook's own files do (86587,5 becomes "86587,5").
const num = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!isFinite(n)) return "";
  return Number.isInteger(n) ? String(n) : String(n).replace(".", ",");
};

// "France ou Pays-Bas": driven by the billing entity, Le Collectif being the French one.
// Anything else — including rows with no organisation — counts as NL.
const frNl = (organisationName) => (/collectif/i.test(organisationName || "") ? "FRANCE" : "PAYS-BAS");

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (!/[",\n]/.test(str)) return str;
  return `"${str.replace(/"/g, '""')}"`;
};

// Comma-separated, no BOM — matches the workbook's exports, and Sheets reads UTF-8 without one.
const downloadCSV = (filename, columns, rows) => {
  const header = columns.map((column) => csvCell(column.label)).join(",");
  const body = rows.map((row) => columns.map((column) => csvCell(column.value(row))).join(",")).join("\n");

  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Date bounds for the endpoints that take them. `year` is "" for the whole history.
const range = (year) => (year ? { dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` } : {});

const searchProjects = async (business_unit_id) => {
  const query = { per_page: 20000 };
  if (business_unit_id) query.business_unit_id = business_unit_id;
  const { ok, data } = await api.post("/project/search", query);
  if (!ok) return null;
  return data?.projects || [];
};

const DATASETS = [
  {
    key: "users",
    file: "PILOTAGE - DATA USERS.csv",
    label: "Users",
    description: "Effectif complet, entrées et sorties comprises. Contient les salaires — ignore le filtre année.",
    fetch: async ({ business_unit_id }) => {
      const query = { per_page: 20000 };
      if (business_unit_id) query.business_unit_id = business_unit_id;
      const { ok, data } = await api.post("/user/search", query);
      if (!ok) return null;
      return data?.users || data || [];
    },
    columns: [
      { label: "ID", value: (e) => e._id },
      { label: "Name", value: (e) => e.name },
      { label: "Starting Date", value: (e) => dt(e.starting_date) },
      { label: "Exit Date", value: (e) => dt(e.exit_date) },
      { label: "Total Monthly Cost (€)", value: (e) => num(e.total_monthly_cost) },
      { label: "Contract", value: (e) => e.contract },
      { label: "Payment Type", value: (e) => e.payment_type },
      { label: "Division", value: (e) => e.division },
      { label: "Office", value: (e) => e.office },
      { label: "Brut Salary", value: (e) => num(e.brut_salary) },
      { label: "Tj Ms", value: (e) => num(e.tjms) },
      { label: "France ou Pays bas", value: (e) => frNl(e.organisation_name) },
    ],
  },
  {
    key: "projects",
    file: "PILOTAGE - DATA PROJECTS.csv",
    label: "Projects",
    description: "Tous les projets avec lead et apporteur d'affaires. Ignore le filtre année.",
    fetch: async ({ business_unit_id }) => searchProjects(business_unit_id),
    columns: [
      { label: "ID", value: (e) => e._id },
      { label: "Name", value: (e) => e.name },
      { label: "Lead Name", value: (e) => e.lead_name },
      { label: "Sales Business Provider Name", value: (e) => e.sales_business_provider_name },
      { label: "Organisation Name", value: (e) => e.organisation_name },
      { label: "FRANCE ou PAYS-BAS", value: (e) => frNl(e.organisation_name) },
    ],
  },
  {
    key: "budgets",
    file: "PILOTAGE - DATA BUDGETS.csv",
    label: "Budgets",
    description: "Budgets avec montant vendu et consommé. Le pays vient du projet — un budget ne porte pas d'organisation.",
    fetch: async ({ year, business_unit_id }) => {
      const query = { limit: 20000 };
      if (year) query.activeYear = year;
      if (business_unit_id) query.business_unit_id = business_unit_id;

      const [budgetRes, projects] = await Promise.all([api.post("/budget/search", query), searchProjects(business_unit_id)]);
      if (!budgetRes.ok) return null;

      // A budget carries no organisation, so the country is resolved through its project.
      const orgByProject = {};
      for (const project of projects || []) orgByProject[project._id] = project.organisation_name;

      return (budgetRes.data || []).map((budget) => ({ ...budget, organisation_name: orgByProject[budget.projectId] }));
    },
    columns: [
      { label: "ID", value: (e) => e._id },
      { label: "Amount", value: (e) => num(e.amount) },
      { label: "AmountUsed", value: (e) => num(e.amountUsed) },
      { label: "ProjectName", value: (e) => e.projectName },
      { label: "Name", value: (e) => e.name },
      { label: "Type", value: (e) => e.type },
      { label: "Confidence", value: (e) => num(e.confidence) },
      { label: "Status", value: (e) => e.status },
      { label: "StartAt", value: (e) => dt(e.startAt) },
      { label: "EndAt", value: (e) => dt(e.endAt) },
      { label: "ProjectId", value: (e) => e.projectId },
      { label: "Sales Type", value: (e) => e.sales_type },
      { label: "CreatedAt", value: (e) => dt(e.createdAt) },
      { label: "France ou Pays-Bas ?", value: (e) => frNl(e.organisation_name) },
    ],
  },
  {
    key: "invoices",
    file: "PILOTAGE - DATA INVOICES.csv",
    label: "Invoices",
    description: "Factures avec net total, TVA, date d'envoi et date d'encaissement.",
    fetch: async ({ year, business_unit_id }) => {
      const query = { ...range(year), limit: 20000, page: 1 };
      if (business_unit_id) query.business_unit_id = business_unit_id;
      const { ok, data } = await api.post("/invoice/search", query);
      if (!ok) return null;
      return data || [];
    },
    columns: [
      { label: "ID", value: (e) => e._id },
      { label: "Date", value: (e) => dt(e.date) },
      { label: "Received", value: (e) => e.received },
      { label: "NetTotal", value: (e) => num(e.netTotal) },
      { label: "ProjectName", value: (e) => e.projectName },
      { label: "TaxValue", value: (e) => num(e.taxValue) },
      { label: "SentAt", value: (e) => dt(e.sentAt) },
      { label: "ReceivedAt", value: (e) => dt(e.receivedAt) },
      { label: "ProjectId", value: (e) => e.projectId },
      { label: "FRANCE ou PAYS-BAS", value: (e) => frNl(e.organisation_name) },
    ],
  },
  {
    key: "activities",
    file: "PILOTAGE - DATA ACTIVITIES.csv",
    label: "Activities",
    description: "Temps déclaré par personne, projet et mois, avec le coût et la valeur associés.",
    fetch: async ({ year, business_unit_id }) => {
      const query = { ...range(year) };
      if (business_unit_id) query.business_unit_id = business_unit_id;
      const { ok, data } = await api.post("/activity/search", query);
      if (!ok) return null;
      return data || [];
    },
    columns: [
      { label: "ID", value: (e) => e._id },
      { label: "Date", value: (e) => dt(e.date) },
      { label: "UserName", value: (e) => e.userName },
      { label: "Total", value: (e) => num(e.total) },
      { label: "Cost(€)", value: (e) => num(e.cost) },
      { label: "BudgetName", value: (e) => e.budgetName },
      { label: "Value", value: (e) => num(e.value) },
      { label: "ProjectName", value: (e) => e.projectName },
      { label: "UserContract", value: (e) => e.userContract },
      { label: "ProjectType", value: (e) => e.projectType },
      { label: "BudgetId", value: (e) => e.budgetId },
      { label: "UserId", value: (e) => e.userId },
      { label: "UserPaymentType", value: (e) => e.userPaymentType },
      { label: "ProjectId", value: (e) => e.projectId },
      { label: "FRANCE ou PAYS BAS", value: (e) => frNl(e.organisation_name) },
      { label: "budgetType", value: (e) => e.budgetType },
    ],
  },
  {
    key: "banks",
    file: "PILOTAGE - DATA BANKS.csv",
    label: "Banks",
    description: "Lignes bancaires avec catégorie, projet et budget. Le type de budget vient d'une jointure sur les budgets.",
    fetch: async ({ year, business_unit_id }) => {
      const query = { limit: 20000, per_page: 20000, page: 1 };
      if (year) {
        query.startDate = `${year}-01-01`;
        query.endDate = `${year}-12-31`;
      }
      if (business_unit_id) query.business_unit_id = business_unit_id;

      const budgetQuery = { limit: 20000, withAmountUsed: false };
      if (business_unit_id) budgetQuery.business_unit_id = business_unit_id;

      const [bankRes, budgetRes] = await Promise.all([api.post("/bank/search", query), api.post("/budget/search", budgetQuery)]);
      if (!bankRes.ok) return null;

      // A bank line stores its budget's id and name but not its type.
      const typeByBudget = {};
      for (const budget of budgetRes.ok ? budgetRes.data || [] : []) typeByBudget[budget._id] = budget.type;

      return (bankRes.data?.banks || []).map((bank) => ({ ...bank, budgetType: typeByBudget[bank.budgetId] }));
    },
    columns: [
      { label: "Date", value: (e) => dt(e.date) },
      { label: "Name", value: (e) => e.name },
      { label: "Description", value: (e) => e.description },
      { label: "Amount (€)", value: (e) => num(e.amount) },
      { label: "Category", value: (e) => e.category },
      { label: "Sub Category", value: (e) => e.sub_category },
      { label: "ProjectName", value: (e) => e.projectName },
      { label: "BudgetName", value: (e) => e.budgetName },
      { label: "Month", value: (e) => dt(e.month) },
      { label: "BudgetId", value: (e) => e.budgetId },
      { label: "Source", value: (e) => e.source },
      { label: "BudgetType", value: (e) => e.budgetType },
      { label: "France ou Pays-Bas", value: (e) => frNl(e.organisation_name) },
    ],
  },
];

export default function Export() {
  const { businessUnit } = useSelector((state) => state.Auth);
  const [year, setYear] = useState("");
  // Keys currently exporting. A list rather than a single key, so one running export only greys
  // out its own button instead of the whole page.
  const [running, setRunning] = useState([]);
  const [allProgress, setAllProgress] = useState(null);

  // Fetch and download one dataset. Returns the row count — 0 when there is nothing to write.
  const runExport = async (dataset) => {
    setRunning((prev) => [...prev, dataset.key]);
    try {
      const business_unit_id = businessUnit?._id && businessUnit._id !== "ALL" ? businessUnit._id : null;
      const rows = await dataset.fetch({ year, business_unit_id });
      if (!rows) throw new Error("FETCH_FAILED");
      if (!rows.length) return 0;

      downloadCSV(dataset.file, dataset.columns, rows);
      return rows.length;
    } finally {
      setRunning((prev) => prev.filter((key) => key !== dataset.key));
    }
  };

  const onExport = async (dataset) => {
    try {
      const count = await runExport(dataset);
      if (!count) return toast.error(`Aucune ligne pour ${dataset.label}`);
      if (dataset.key === "activities" && count >= ACTIVITY_LIMIT) {
        return toast.error(`Export plafonné à ${ACTIVITY_LIMIT} lignes — le fichier est incomplet, filtre par année`);
      }
      toast.success(`${count} lignes exportées`);
    } catch (error) {
      console.log(error);
      toast.error(`Échec de l'export : ${dataset.label}`);
    }
  };

  // One after the other on purpose: budgets triggers a per-budget computation server-side and banks
  // pulls the whole ledger, so firing the six at once would hammer the API without arriving sooner.
  const onExportAll = async () => {
    const failed = [];
    setAllProgress(0);
    for (let i = 0; i < DATASETS.length; i++) {
      const dataset = DATASETS[i];
      try {
        const count = await runExport(dataset);
        if (!count) failed.push(dataset.label);
      } catch (error) {
        console.log(error);
        failed.push(dataset.label);
      }
      setAllProgress(i + 1);
    }
    setAllProgress(null);
    if (failed.length) return toast.error(`Échec : ${failed.join(", ")}`);
    toast.success(`${DATASETS.length} fichiers exportés`);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Export</h1>
          <p className="text-sm text-gray-500">
            Les 6 fichiers du classeur PILOTAGE, aux mêmes colonnes et au même format. Périmètre : {businessUnit?.name || "All Teams"} —{" "}
            {year || "toutes les années"}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select className="input w-44" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Toutes les années</option>
            {YEARS.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          <button className="blue-btn flex items-center gap-2 whitespace-nowrap" disabled={allProgress !== null} onClick={onExportAll}>
            <MdFileDownload className="text-lg" />
            {allProgress !== null ? `Export ${allProgress}/${DATASETS.length}...` : "Tout télécharger"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DATASETS.map((dataset) => (
          <div key={dataset.key} className="bg-white border border-neutral-300 rounded-md p-4 flex flex-col justify-between gap-4">
            <div>
              <h2 className="font-semibold">{dataset.label}</h2>
              <p className="text-sm text-gray-500 mt-1">{dataset.description}</p>
              <p className="text-xs text-gray-400 mt-2">
                {dataset.columns.length} colonnes · {dataset.file}
              </p>
            </div>
            <button
              className="blue-btn flex items-center justify-center gap-2 w-full"
              disabled={running.includes(dataset.key) || allProgress !== null}
              onClick={() => onExport(dataset)}>
              <MdFileDownload className="text-lg" />
              {running.includes(dataset.key) ? "Export en cours..." : "Export CSV"}
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400">
        Séparateur virgule, décimales à la virgule, dates en J/M/AAAA, colonne ID en premier. Seules les colonnes source sont exportées : les colonnes calculées du classeur (SURCONSO ?, diff en
        jours, Concat, ExitDateFixed…) restent dans le sheet. « Tout télécharger » enchaîne les six fichiers un par un — le navigateur demande l'autorisation au deuxième téléchargement.
      </p>
    </div>
  );
}
